import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "../App";
import {
  Send,
  AlertTriangle,
  Home,
  Briefcase,
  Shield,
  Users,
  FileText,
  Building,
  Bot,
  User,
  Loader2,
  RefreshCw,
  Sparkles,
  UserPlus,
  X,
} from "lucide-react";

const AIAssistant = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [reqForm, setReqForm] = useState({ summary: "", location: "", category: "Family Law", urgency: "medium", is_wallet_eligible: false });
  const [reqBusy, setReqBusy] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const topics = [
    { id: "housing", label: "Housing & Tenancy", icon: Home },
    { id: "employment", label: "Employment", icon: Briefcase },
    { id: "police", label: "Police & Rights", icon: Shield },
    { id: "family", label: "Family Law", icon: Users },
    { id: "contracts", label: "Contracts", icon: FileText },
    { id: "business", label: "Business", icon: Building },
  ];

  const samplePrompts = [
    "My landlord wants to evict me without notice, what can I do?",
    "I was stopped by police and I think my rights were violated.",
    "My employer hasn't paid my salary for 2 months.",
    "How do I write a simple contract for services?",
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  useEffect(() => {
    setMessages([
      {
        role: "assistant",
        content: `Hello — I am Justice-AI, your preliminary legal guidance assistant.

**Important:** I provide general legal information, not legal advice. For specific legal matters, a verified lawyer may be assigned to review your case.

How can I help you today? You can:
- Ask about your legal rights
- Get guidance on legal procedures
- Learn about available legal remedies

Pick a topic on the left or type your question below.`,
      },
    ]);
  }, []);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;
    const userMessage = inputMessage.trim();
    setInputMessage("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API}/chat`,
        { message: userMessage, session_id: sessionId, topic: selectedTopic },
        { withCredentials: true }
      );
      setSessionId(response.data.session_id);
      setMessages((prev) => [...prev, { role: "assistant", content: response.data.response }]);
    } catch (error) {
      console.error("Chat error:", error);
      toast.error("Failed to get response. Please try again.");
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "I apologize, but I encountered an error. Please try again or rephrase your question." },
      ]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handlePromptClick = (prompt) => {
    setInputMessage(prompt);
    inputRef.current?.focus();
  };

  const handleNewChat = () => {
    setSessionId(null);
    setSelectedTopic(null);
    setMessages([
      {
        role: "assistant",
        content: `Hello — I am Justice-AI. How can I help you today?\n\n**Note:** I provide general legal information, not legal advice.`,
      },
    ]);
  };

  const submitLawyerRequest = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please sign in to request a lawyer.");
      navigate("/login", { state: { from: { pathname: "/ai-assistant" } } });
      return;
    }
    if (!reqForm.summary.trim() || !reqForm.location.trim()) {
      toast.error("Please describe your matter and location.");
      return;
    }
    setReqBusy(true);
    try {
      await axios.post(`${API}/lawyer-requests`, reqForm, { withCredentials: true });
      toast.success("Request submitted — verified lawyers will be notified shortly.");
      setShowRequestModal(false);
      setReqForm({ summary: "", location: "", category: "Family Law", urgency: "medium", is_wallet_eligible: false });
      // Take the citizen to their requests dashboard
      setTimeout(() => navigate("/my-requests"), 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit request");
    } finally {
      setReqBusy(false);
    }
  };

  return (
    <main className="flex-1 bg-ink text-white min-h-[calc(100vh-5rem)]" data-testid="ai-assistant-page">
      <div className="container mx-auto px-5 sm:px-8 py-10 max-w-7xl">
        <div className="mb-8">
          <div className="eyebrow mb-3">Justice-AI Assistant</div>
          <h1 className="font-playfair text-3xl md:text-5xl text-white leading-tight">
            Tell us what happened.{" "}
            <span className="italic text-aj-gradient">In plain language.</span>
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <aside className="lg:col-span-1 space-y-5">
            <div className="glass rounded-2xl p-5 border-l-2 border-l-gold-400" data-testid="disclaimer-card">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-gold-300 mt-0.5 shrink-0" />
                <div>
                  <h4 className="font-montserrat text-[11px] uppercase tracking-[0.2em] text-gold-200 mb-2">Important Notice</h4>
                  <p className="font-inter text-xs text-white/65 leading-relaxed">
                    Justice-AI provides preliminary legal information only. This is not legal advice and does not create an attorney-client relationship.
                  </p>
                </div>
              </div>
            </div>

            <div className="glass rounded-2xl p-5" data-testid="topics-card">
              <h4 className="font-montserrat text-[11px] uppercase tracking-[0.2em] text-turquoise mb-4 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> Select Topic
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {topics.map((topic) => (
                  <button
                    key={topic.id}
                    onClick={() => setSelectedTopic(topic.id)}
                    data-testid={`topic-${topic.id}`}
                    className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-[11px] font-montserrat font-semibold uppercase tracking-wider transition-all ${
                      selectedTopic === topic.id
                        ? "bg-turquoise text-ink"
                        : "bg-white/[0.03] text-white/70 border border-white/8 hover:border-turquoise/40 hover:text-white"
                    }`}
                  >
                    <topic.icon className="w-3.5 h-3.5" />
                    <span className="truncate text-left">{topic.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="glass rounded-2xl p-5" data-testid="quick-actions-card">
              <h4 className="font-montserrat text-[11px] uppercase tracking-[0.2em] text-white/75 mb-4">Quick Actions</h4>
              <div className="space-y-2">
                <button onClick={handleNewChat} data-testid="new-chat-btn" className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-montserrat font-semibold uppercase tracking-wider text-white/80 border border-white/10 hover:border-turquoise/40 hover:bg-white/5 transition-all">
                  <RefreshCw className="w-3.5 h-3.5" /> New Conversation
                </button>
                <Link to="/get-legal-help" data-testid="templates-btn" className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-montserrat font-semibold uppercase tracking-wider text-white/80 border border-white/10 hover:border-turquoise/40 hover:bg-white/5 transition-all">
                  <FileText className="w-3.5 h-3.5" /> Legal Templates
                </Link>
                {!user && (
                  <Link to="/login" data-testid="login-save-btn" className="btn-aj-primary w-full">
                    <User className="w-4 h-4" /> Login to Save
                  </Link>
                )}
              </div>
            </div>
          </aside>

          {/* Chat panel */}
          <div className="lg:col-span-3">
            <div className="glass-strong rounded-2xl h-[calc(100vh-15rem)] flex flex-col overflow-hidden" data-testid="chat-card">
              {/* Header */}
              <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-turquoise/15 border border-turquoise/30 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-turquoise" />
                  </div>
                  <div>
                    <h3 className="font-playfair text-lg text-white">Justice-AI</h3>
                    <p className="font-inter text-[11px] text-white/50">
                      {selectedTopic ? `Topic: ${topics.find((t) => t.id === selectedTopic)?.label}` : "General Legal Guidance"}
                    </p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-turquoise/10 border border-turquoise/30 font-montserrat text-[10px] uppercase tracking-wider text-turquoise">
                  <span className="w-1.5 h-1.5 bg-turquoise rounded-full animate-pulse" />
                  Online
                </span>
              </div>

              {/* Request a Lawyer banner */}
              <div className="px-5 py-2.5 border-b border-white/8 bg-gold-400/[0.04] flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2.5">
                  <UserPlus className="w-4 h-4 text-gold-300 shrink-0" />
                  <span className="font-inter text-xs text-white/75">
                    Need a verified lawyer for this matter?
                  </span>
                </div>
                <button
                  onClick={() => {
                    if (!user) {
                      toast.error("Please sign in to request a lawyer.");
                      navigate("/login", { state: { from: { pathname: "/ai-assistant" } } });
                      return;
                    }
                    setShowRequestModal(true);
                  }}
                  data-testid="request-lawyer-btn"
                  className="px-4 py-1.5 rounded-full bg-gold-400/15 border border-gold-400/40 hover:bg-gold-400/25 text-gold-200 font-montserrat text-[10px] uppercase tracking-wider font-semibold transition-all"
                >
                  Request a Lawyer
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-5 py-6">
                <div className="space-y-4">
                  {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`message-${i}`}>
                      <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                        m.role === "user"
                          ? "bg-turquoise text-ink rounded-br-sm"
                          : "bg-white/[0.04] border border-white/8 text-white rounded-bl-sm"
                      }`}>
                        <div className="flex items-start gap-2">
                          {m.role === "assistant" && <Bot className="w-4 h-4 text-turquoise mt-0.5 shrink-0" />}
                          <div className="text-sm leading-relaxed whitespace-pre-wrap font-inter">{m.content}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="flex justify-start" data-testid="loading-indicator">
                      <div className="bg-white/[0.04] border border-white/8 rounded-2xl rounded-bl-sm px-4 py-3">
                        <div className="flex items-center gap-2 text-white/70">
                          <Bot className="w-4 h-4 text-turquoise" />
                          <Loader2 className="w-4 h-4 animate-spin text-turquoise" />
                          <span className="font-inter text-sm">Thinking…</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              {messages.length <= 1 && (
                <div className="px-5 py-3 border-t border-white/8 bg-black/30">
                  <p className="font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Try asking:</p>
                  <div className="flex flex-wrap gap-2">
                    {samplePrompts.map((p, i) => (
                      <button
                        key={i}
                        onClick={() => handlePromptClick(p)}
                        data-testid={`sample-prompt-${i}`}
                        className="font-inter text-xs px-3 py-1.5 rounded-full bg-white/[0.03] border border-white/10 text-white/70 hover:border-turquoise/40 hover:text-turquoise transition-colors"
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input */}
              <div className="px-5 py-4 border-t border-white/8">
                <div className="flex items-center gap-3">
                  <input
                    ref={inputRef}
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder="Ask about your legal rights…"
                    disabled={isLoading}
                    data-testid="chat-input"
                    className="flex-1 bg-white/[0.04] border border-white/10 rounded-full px-5 py-3 font-inter text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-turquoise/60 focus:bg-white/[0.06] transition-all"
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!inputMessage.trim() || isLoading}
                    data-testid="send-btn"
                    className="w-12 h-12 rounded-full bg-turquoise text-ink flex items-center justify-center hover:bg-turquoise-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:shadow-[0_0_24px_rgba(0,200,215,0.4)]"
                  >
                    {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Request a Lawyer Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm" data-testid="request-lawyer-modal">
          <div className="relative w-full max-w-lg glass-strong rounded-3xl p-8 max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowRequestModal(false)}
              data-testid="close-request-modal"
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/5 text-white/60 hover:text-white transition-all"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="aj-chip aj-chip-gold mb-4">Request a Verified Lawyer</div>
            <h2 className="font-playfair text-2xl text-white mb-2">Tell us about your matter.</h2>
            <p className="font-inter text-xs text-white/55 mb-6">
              Your request will be matched to the most suitable verified lawyer by expertise,
              location and urgency.
            </p>
            <form onSubmit={submitLawyerRequest} className="space-y-4">
              <div>
                <label className="block font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Brief Summary</label>
                <textarea
                  rows={4}
                  value={reqForm.summary}
                  onChange={(e) => setReqForm({ ...reqForm, summary: e.target.value })}
                  placeholder="Describe what happened in plain language…"
                  required
                  data-testid="req-summary"
                  className="w-full bg-white/[0.04] border border-white/10 rounded-2xl px-4 py-3 font-inter text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-turquoise/60 resize-none"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Category</label>
                  <select
                    value={reqForm.category}
                    onChange={(e) => setReqForm({ ...reqForm, category: e.target.value })}
                    data-testid="req-category"
                    className="w-full bg-white/[0.04] border border-white/10 rounded-full px-4 py-3 font-inter text-sm text-white focus:outline-none focus:border-turquoise/60"
                  >
                    {["Family Law", "Domestic Violence", "Criminal Defence", "Land / Property", "Employment", "Contract Dispute", "Human Rights", "Tenancy", "Consumer", "Immigration", "Other"].map((c) => (
                      <option key={c} value={c} className="bg-ink">{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Urgency</label>
                  <select
                    value={reqForm.urgency}
                    onChange={(e) => setReqForm({ ...reqForm, urgency: e.target.value })}
                    data-testid="req-urgency"
                    className="w-full bg-white/[0.04] border border-white/10 rounded-full px-4 py-3 font-inter text-sm text-white focus:outline-none focus:border-turquoise/60"
                  >
                    <option value="low" className="bg-ink">Low</option>
                    <option value="medium" className="bg-ink">Medium</option>
                    <option value="high" className="bg-ink">High</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Location</label>
                <input
                  type="text"
                  value={reqForm.location}
                  onChange={(e) => setReqForm({ ...reqForm, location: e.target.value })}
                  placeholder="City, State / Country"
                  required
                  data-testid="req-location"
                  className="w-full bg-white/[0.04] border border-white/10 rounded-full px-4 py-3 font-inter text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-turquoise/60"
                />
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={reqForm.is_wallet_eligible}
                  onChange={(e) => setReqForm({ ...reqForm, is_wallet_eligible: e.target.checked })}
                  data-testid="req-wallet"
                  className="w-4 h-4 accent-turquoise"
                />
                <span className="font-inter text-xs text-white/75">
                  I cannot afford full legal fees — request <span className="text-gold-300">Justice Wallet</span> consideration.
                </span>
              </label>
              <button
                type="submit"
                disabled={reqBusy}
                data-testid="req-submit"
                className="btn-aj-primary w-full !py-3.5 disabled:opacity-60"
              >
                {reqBusy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>) : (<>Send Request to Verified Lawyers <UserPlus className="w-4 h-4" /></>)}
              </button>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};

export default AIAssistant;
